import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditIcon from '@material-ui/icons/Edit'
import GetAppIcon from '@material-ui/icons/GetApp'
import PaletteIcon from '@material-ui/icons/Palette'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import RefreshIcon from '@material-ui/icons/Refresh'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import React, { useCallback, useEffect, useRef, useState } from 'react'

// Page 1 of the team-wall spec: the finished wall at a centre. Shown in the
// print dialog because "make a wall like this" lands in one glance where a
// paragraph does not.
import teamWallReference from 'assets/images/teamwall/team_wall_reference.jpg'

import {
  fetchScopedCentres,
  fetchTeamWallDraft,
  fetchTeamWallDrafts,
  fetchTeamWallHqMembers,
  fetchTeamWallKekaShifts,
  importTeamWallIdForm,
  importTeamWallIdFormAll,
  importTeamWallIdPhoto,
  saveTeamWallDraft,
  updateTeamWallDraft
} from 'api'
import constants from 'app/constants'
import {
  CARD_H,
  CARD_MM,
  CARD_W,
  IdCardBack,
  IdCardFront,
  guessSection
} from 'app/pages/Operations/TeamWall/IdCard'
import { showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

// Persist the picked centre across visits (mirrors ParentOnboarding).
const CENTRE_KEY = 'teamwall.centre'

// The roster opens on every centre. NOTE this is NOT the same shape as the
// newsletter's all-centres list: a roster is ONE draft per centre with the
// photos inlined as data URLs — 16 MB across the 10 centres that have one — so
// "all centres" CANNOT just load them all. It reads the HQ members endpoint
// instead (same data, photos stripped to a has_photo flag) and is a read-only
// overview; picking a centre loads that centre's real draft and unlocks editing.
const ALL_CENTRES = 'all'

// Poster artboard geometry — taken from the Canva master ("Template | Teacher
// & Team Wall", copy DAHO9KM_50k): A4 portrait at 794x1123 px. All poster
// styles are in artboard pixels; previews scale visually and the PDF is
// captured at natural size, so both stay true to the design.
const BOARD_W = 794
const BOARD_H = 1123

// Exact palette sampled from the Canva master's exports.
const BLUE = '#58B7E6'
const ORANGE = '#F5A066'
const PANEL = '#F7F7F7'
const INK = '#39393B'

// SOP print size (mm): every poster prints on a 6x4" photo sheet ("4R"),
// which is what centres have been given — no lamination. The artboard is
// A-series (√2) aspect while 4R is 2:3, so downloadPdf contain-fits the
// poster inside the page (centred, thin white bands) — never stretched.
const PHOTO_4X6 = [101.6, 152.4]

// The three roster categories. The poster template follows the category
// (teachers = blue cloud+bio, centre team = orange cloud+bio, support =
// orange name+pill only) so picking the category picks the design.
const SECTIONS = [
  {
    key: 'teachers',
    label: 'Teachers',
    short: 'Teacher',
    color: BLUE,
    hasBio: true,
    pageSize: PHOTO_4X6
  },
  {
    key: 'centre_team',
    label: 'Centre Team',
    short: 'Centre team',
    color: ORANGE,
    hasBio: true,
    pageSize: PHOTO_4X6
  },
  {
    key: 'support',
    label: 'Support Team',
    short: 'Support',
    color: ORANGE,
    hasBio: false,
    pageSize: PHOTO_4X6
  }
]
const SECTION_BY_KEY = Object.fromEntries(SECTIONS.map((s) => [s.key, s]))

// Role-tag choices per category (posters render them lowercase). "Other…"
// in the dialog reveals a free-text field for anything not listed.
const ROLE_OPTIONS = {
  teachers: [
    'preschool',
    'explore',
    'music',
    'dance',
    'art',
    'sports',
    'public speaking',
    'keyboard'
  ],
  centre_team: ['centre director', 'centre coordinator', 'centre manager'],
  support: ['housekeeping', 'security', 'caretaker']
}

const blankMember = (section = 'teachers') => ({
  section,
  name: '',
  role: '',
  heading: '',
  bio: '',
  phone: '',
  email: '',
  emergencyName: '',
  emergencyPhone: '',
  cardTitle: '',
  photo: '',
  wallPhoto: '',
  photoRef: '',
  photoFaces: null,
  // 'form' = pulled from the ID form (server face-crops it), 'manual' = someone
  // uploaded it here. photoV stamps the processing version: form photos below
  // PHOTO_VERSION are re-pulled by an import (early ones were NOT face-cropped
  // — Riva's full-body card), manual photos are never touched.
  photoSource: '',
  photoV: 0,
  active: true
})

const PHOTO_VERSION = 2

// Placeholder junk typed into required form fields ("NA" as a name/bio).
const isJunk = (v) =>
  ['na', 'n/a', 'n.a', 'n.a.', 'nil', 'none', 'nan', '-', '--', '.'].includes(
    String(v || '')
      .trim()
      .toLowerCase()
  )

// Members saved before the active flag existed count as active.
const isActive = (m) => m.active !== false

// Keka names are matched case- and spacing-blind, as the API keys them.
const shiftKey = (name) =>
  (name || '').trim().replace(/\s+/g, ' ').toLowerCase()
// The roster name as written, else its first and last word ("Riya Mary Rao"
// against Keka's "Riya Rao").
const shiftOf = (shifts, name) => {
  const key = shiftKey(name)
  const words = key.split(' ')
  return (
    shifts[key] ||
    (words.length > 2 ? shifts[`${words[0]} ${words[words.length - 1]}`] : '')
  )
}



// Full-bleed poster photos: keep more pixels than the newsletter's inline
// shots, but still bounded so drafts stay a sane size.
const MAX_PHOTO_DIM = 1600

const useStyles = makeStyles((theme) => ({
  boardRoot: { padding: theme.spacing(3) },
  boardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  bioCell: {
    maxWidth: 260,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  contactCell: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  photoThumb: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`
  },
  dialogField: { marginBottom: theme.spacing(2) },
  // overflow:'hidden' CLIPPED the table — the Actions column fell off the right
  // edge with no way to reach it (you had to zoom the browser out). Scroll the
  // card horizontally instead, and hold the table to a width its columns
  // actually fit in, so narrow screens scroll rather than crush the content.
  // The row that was just added — held for a few seconds so the eye lands on it.
  justAddedRow: {
    animation: '$flashRow 4s ease-out',
  },
  '@keyframes flashRow': {
    '0%': { backgroundColor: 'rgba(239, 93, 52, 0.28)' },
    '70%': { backgroundColor: 'rgba(239, 93, 52, 0.18)' },
    '100%': { backgroundColor: 'transparent' },
  },
  rosterCard: {
    overflowX: 'auto',
    // The roster scrolls inside its own box so the header stays pinned (Table
    // stickyHeader). maxHeight is measured at runtime to reach exactly the
    // viewport bottom (see rosterRef) -- a fixed calc() left dead space below.
    overflowY: 'auto',
    '& .MuiTableCell-stickyHeader': { backgroundColor: '#fff' }
  },
  rosterTable: { minWidth: 1040 },
  wallReference: {
    width: '100%',
    display: 'block',
    borderRadius: 6,
    border: '1px solid rgba(0,0,0,0.12)'
  },
  // Live template preview inside the add/edit dialog + the per-row design
  // dialog: a real poster node scaled down inside a clipped frame.
  previewFrame: {
    overflow: 'hidden',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[200],
    flexShrink: 0
  },
  previewInner: { transformOrigin: 'top left' },
  // Off-screen render target for PDF downloads.
  hiddenStack: {
    position: 'fixed',
    top: 0,
    left: -9999,
    zIndex: -1,
    pointerEvents: 'none'
  },
  posterStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: 16
  },
  poster: {
    width: BOARD_W,
    height: BOARD_H,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#E8E4DE',
    flexShrink: 0,
    fontFamily: 'AvenirRounded, Avenir, sans-serif'
  },
  // The photo fills only the visible area above the white panel (height set
  // inline per poster) instead of the whole portrait board — a landscape
  // photo then crops gently instead of zooming into a face.
  posterPhoto: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    // background-size, not object-fit — html2canvas ignores object-fit and
    // would stretch the photo into the box when building the PDF.
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    backgroundRepeat: 'no-repeat'
  },
  posterPhotoEmpty: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9B968E',
    fontSize: 28,
    fontWeight: 500
  },
  panel: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: PANEL
  },
  cloud: {
    position: 'absolute',
    left: 26,
    top: 16,
    width: 264,
    height: 234
  },
  cloudText: {
    position: 'absolute',
    left: 26,
    top: 16,
    width: 264,
    height: 234,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: '#fff',
    fontWeight: 600,
    padding: '0 57px',
    boxSizing: 'border-box',
    lineHeight: 1.18,
    // Only break a word when it genuinely can't fit on its own line (e.g. a
    // very long "Psychologist" wider than the blob) — a normal word like
    // "centre" must stay whole, not hyphenate into "cen-tre" to fill a line.
    overflow: 'hidden',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    hyphens: 'none'
  },
  pill: {
    position: 'absolute',
    color: '#fff',
    fontWeight: 600,
    textTransform: 'lowercase',
    whiteSpace: 'nowrap'
  },
  name: {
    position: 'absolute',
    color: INK,
    fontWeight: 600,
    textTransform: 'lowercase',
    lineHeight: 1
  },
  bio: {
    position: 'absolute',
    color: INK,
    fontWeight: 500,
    whiteSpace: 'pre-wrap'
  }
}))

/* ------------------------------ helpers ------------------------------ */

// Crop thin near-uniform edge bands (letterboxing / frame edges baked into
// uploads) — up to 12% per side; uniform = low pixel variance in the band.
const trimBorders = (img) => {
  const probe = document.createElement('canvas')
  probe.width = img.width
  probe.height = img.height
  const ctx = probe.getContext('2d')
  ctx.drawImage(img, 0, 0)
  const uniform = (x, y, w, h) => {
    if (w < 1 || h < 1) return false
    const d = ctx.getImageData(x, y, w, h).data
    let sum = 0
    let sumSq = 0
    const n = d.length / 4
    for (let i = 0; i < d.length; i += 4) {
      const l = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
      sum += l
      sumSq += l * l
    }
    const mean = sum / n
    return Math.sqrt(Math.max(0, sumSq / n - mean * mean)) < 18
  }
  const stepX = Math.max(2, Math.round(img.width / 100))
  const stepY = Math.max(2, Math.round(img.height / 100))
  let left = 0
  let right = img.width
  let top = 0
  let bottom = img.height
  while (left < img.width * 0.12 && uniform(left, 0, stepX, img.height))
    left += stepX
  while (right > img.width * 0.88 && uniform(right - stepX, 0, stepX, img.height))
    right -= stepX
  while (top < img.height * 0.12 && uniform(0, top, img.width, stepY))
    top += stepY
  while (bottom > img.height * 0.88 && uniform(0, bottom - stepY, img.width, stepY))
    bottom -= stepY
  if (
    (left || top || right < img.width || bottom < img.height) &&
    right - left > 50 &&
    bottom - top > 50
  ) {
    const out = document.createElement('canvas')
    out.width = right - left
    out.height = bottom - top
    out
      .getContext('2d')
      .drawImage(img, left, top, out.width, out.height, 0, 0, out.width, out.height)
    return out
  }
  return img
}

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const raw = new Image()
      raw.onload = () => {
        let img
        try {
          img = trimBorders(raw)
        } catch (err) {
          img = raw
        }
        const ratio = Math.min(
          1,
          MAX_PHOTO_DIM / Math.max(img.width, img.height)
        )
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * ratio)
        canvas.height = Math.round(img.height * ratio)
        canvas
          .getContext('2d')
          .drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', 0.85))
      }
      raw.onerror = reject
      raw.src = reader.result
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const PhotoPicker = ({ label, onPick }) => {
  const inputRef = useRef(null)
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PhotoCameraIcon />}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const file = (e.target.files || [])[0]
          if (!file) return
          try {
            onPick(await readFile(file))
          } catch (err) {
            showToast('Could not read the image', constants.ERROR)
          }
          e.target.value = ''
        }}
      />
    </>
  )
}

// Name sizes shrink for long names — the Canva master overflows on anything
// longer than ~8 characters (its "ashwathy" already wraps); we don't.
const nameFontSize = (name, base) => {
  const n = (name || '').length
  if (n <= 10) return base
  if (n <= 15) return Math.round(base * 0.8)
  if (n <= 22) return Math.round(base * 0.62)
  return Math.round(base * 0.5)
}

// Bios render inside curly quotes; typing them in the form is optional.
const quoted = (text) => {
  let t = (text || '').trim()
  if (!t) return ''
  // Strip quotes people typed into the form, then add a balanced pair — a
  // pre-existing opening quote used to suppress OUR closing one.
  t = t.replace(/^[“”"']+/, '').replace(/[“”"']+$/, '').trim()
  return `“${t}”`
}

// Long bios overflowed the white panel (the closing quote fell off the
// poster). Shrink with length, and hard-cap so the panel always fits.
const BIO_MAX_CHARS = 430
const bioFontSize = (text) => {
  const n = (text || '').length
  if (n <= 240) return 30
  if (n <= 380) return 26
  return 23
}
const clampBio = (text) => {
  const t = (text || '').trim()
  if (t.length <= BIO_MAX_CHARS) return t
  return `${t.slice(0, BIO_MAX_CHARS - 1).trimEnd()}…`
}

/* --------------------------- poster shapes --------------------------- */

// The wavy top edge of the white bottom panel, traced off the Canva master.
const WaveEdge = ({ top }) => (
  <svg
    style={{ position: 'absolute', left: 0, top, width: '100%', height: 36 }}
    viewBox="0 0 794 36"
    preserveAspectRatio="none"
  >
    <path
      d="M0,26 C40,10 80,34 120,22 C160,10 200,30 240,20
         C280,10 320,32 360,22 C400,12 440,30 480,20
         C520,10 560,32 600,22 C640,12 680,30 720,20
         C760,12 780,20 794,18 L794,36 L0,36 Z"
      fill={PANEL}
    />
  </svg>
)

// The scalloped "cloud" blob behind the heading, built from overlapping
// circles like the Canva original.
const CloudBlob = ({ color }) => (
  <svg viewBox="-14 -8 290 250" style={{ width: '100%', height: '100%' }}>
    <g fill={color}>
      <circle cx="55" cy="70" r="42" />
      <circle cx="125" cy="48" r="52" />
      <circle cx="195" cy="65" r="42" />
      <circle cx="228" cy="125" r="38" />
      <circle cx="196" cy="178" r="40" />
      <circle cx="128" cy="192" r="44" />
      <circle cx="58" cy="172" r="40" />
      <circle cx="28" cy="120" r="38" />
      <circle cx="128" cy="118" r="72" />
    </g>
  </svg>
)

// The cloud's inner text box is ~176px wide (264 − 2×44 padding). Size the
// heading so BOTH the whole string and its longest single word fit — a long
// word (e.g. "Psychologist") is what used to break out of the blob.
// The SAFE box inside the cloud — deliberately smaller than the text div's
// rectangle. The cloud is a BLOB, not a box: at its waist the left edge sits
// well inside the rectangle, so a line that filled the rectangle's width had
// its first letter poking out onto the photo (the stray "P" on Ashwathy's
// "…Psychologist", the "e" on Nancy's). overflow:hidden could never catch that
// — it clips to the rectangle, and the letter was INSIDE the rectangle and
// outside the blob. Size the type to the blob's usable middle instead.
const CLOUD_TEXT_W = 150
const CLOUD_TEXT_H = 150
const HEADING_MAX_CHARS = 60

const clampHeading = (text) => {
  const t = (text || '').trim()
  return t.length <= HEADING_MAX_CHARS
    ? t
    : `${t.slice(0, HEADING_MAX_CHARS - 1).trimEnd()}…`
}

const cloudFontSize = (text) => {
  const t = clampHeading(text)
  if (!t) return 32
  const words = t.split(/\s+/)
  const longest = words.reduce((a, w) => Math.max(a, w.length), 0)
  // avg glyph ≈ 0.56em: the longest word must fit one line...
  const byWord = Math.floor(CLOUD_TEXT_W / (0.56 * Math.max(1, longest)))
  // ...and the whole string must fit the box's ~5 usable lines.
  const bySize = Math.floor(
    Math.sqrt((CLOUD_TEXT_W * CLOUD_TEXT_H) / (0.72 * Math.max(1, t.length)))
  )
  return Math.max(15, Math.min(32, byWord, bySize))
}

// A blank heading used to render an EMPTY cloud on the poster — auto-draft one
// from the role so every poster reads finished (type a real one to override).
const AUTO_HEADINGS = [
  'here for the little wins',
  'learning made joyful',
  'curious minds welcome',
  'every day, a new adventure',
  'growing with the children',
  'small steps, big smiles',
  'making learning feel like play',
  'nurturing young minds'
]

const autoHeading = (member) => {
  const role = (member.role || '').toLowerCase()
  if (/music|keyboard/.test(role)) return 'learning to the beat'
  if (/dance/.test(role)) return 'every step tells a story'
  if (/art/.test(role)) return 'colouring outside the lines'
  if (/sport/.test(role)) return 'play hard, grow strong'
  if (/director|coordinator|manager/.test(role))
    return 'keeping our centre humming'
  const seed = (member.name || '').length + (member.bio || '').length
  return AUTO_HEADINGS[seed % AUTO_HEADINGS.length]
}

/* ------------------------------ posters ------------------------------ */

// One poster, laid out per category (geometry from the Canva master):
// - teachers / centre_team: cloud heading top-left, panel from y=645, role
//   pill on the wave's right, name + quoted bio on the panel.
// - support: panel from y=949, role pill on the wave's right, name only.
const Poster = ({ classes, member }) => {
  const section = SECTION_BY_KEY[member.section] || SECTIONS[0]
  const color = section.color
  const panelTop = section.hasBio ? 645 : 949
  const posterPhoto = member.wallPhoto || member.photo

  return (
    <div className={classes.poster} data-poster>
      {posterPhoto ? (
        // A background-image div, NOT an <img objectFit:'cover'> — html2canvas
        // (which renders the PDF) does not implement object-fit, so an <img>
        // that crops correctly on screen came out STRETCHED in the download: a
        // portrait photo squashed into the 794x657 box. html2canvas does honour
        // background-size, so the PDF now crops exactly like the preview.
        <div
          className={classes.posterPhoto}
          style={{
            height: panelTop + 12,
            backgroundImage: `url("${posterPhoto}")`
          }}
          role="img"
          aria-label={member.name}
        />
      ) : (
        <div
          className={classes.posterPhotoEmpty}
          style={{ height: panelTop + 12 }}
        >
          photo goes here
        </div>
      )}

      <div className={classes.panel} style={{ top: panelTop }} />
      <WaveEdge top={panelTop - 26} />

      {section.hasBio && (
        <>
          <div className={classes.cloud}>
            <CloudBlob color={color} />
          </div>
          <div
            className={classes.cloudText}
            style={{
              fontSize: cloudFontSize(member.heading || autoHeading(member))
            }}
          >
            {clampHeading(member.heading || autoHeading(member))}
          </div>
        </>
      )}

      {member.role ? (
        <div
          className={classes.pill}
          style={{
            right: 40,
            top: panelTop - 48,
            backgroundColor: color,
            fontSize: section.hasBio ? 42 : 36,
            padding: section.hasBio ? '16px 48px' : '14px 40px',
            borderRadius: '44px 44px 44px 12px'
          }}
        >
          {member.role}
        </div>
      ) : null}

      <div
        className={classes.name}
        style={{
          left: section.hasBio ? 79 : 57,
          top: section.hasBio ? panelTop + 52 : panelTop + 48,
          fontSize: nameFontSize(member.name, 64)
        }}
      >
        {member.name}
      </div>

      {section.hasBio && (
        <div
          className={classes.bio}
          style={{
            left: 79,
            top: panelTop + 168,
            width: 690,
            fontSize: bioFontSize(member.bio),
            lineHeight: 1.45
          }}
        >
          {quoted(clampBio(member.bio))}
        </div>
      )}
    </div>
  )
}

// A poster scaled into a clipped frame — used by the dialogs.
// Memoised: the poster is a full A4 render (SVG edges, blobs, photo, font
// autosizing). Re-rendering it on every keystroke of the edit dialog starves the
// main thread and the text fields start DROPPING CHARACTERS — you could not type
// more than two letters into the add-member dialog.
const PosterPreview = React.memo(({ classes, member, scale }) => (
  <div
    className={classes.previewFrame}
    style={{ width: BOARD_W * scale, height: BOARD_H * scale }}
  >
    <div
      className={classes.previewInner}
      style={{ transform: `scale(${scale})` }}
    >
      <Poster classes={classes} member={member} />
    </div>
  </div>
))

/* -------------------------------- page -------------------------------- */

// Centre Team Roster (renamed from Team Wall, 2026-07-11): ONE table of the
// centre's people with a Category column instead of three stacked section
// cards. Poster design + PDF live on each row; "Add team member" captures
// the full record (incl. phone/email) with the matching template previewed
// live. One living roster per centre: the most recently modified draft is
// loaded and saved back (created as "Team Wall" when none exists yet).
const TeamWall = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  // False once the user is mapped to a centre — "all centres" then means nothing
  // to them, so the option is not offered.
  const [unrestricted, setUnrestricted] = useState(true)
  // Shared by every Centre Operations page and kept across refreshes.
  const [centreUuid, setCentreUuid, pickCentre] = useOpsCentre({
    legacyKey: CENTRE_KEY
  })

  const [members, setMembers] = useState([])
  const [draftUuid, setDraftUuid] = useState('')
  const [draftName, setDraftName] = useState('Team Wall')
  const [loadingWall, setLoadingWall] = useState(false)
  const [saving, setSaving] = useState(false)

  // Member edit dialog: index into members, or -1 closed; `editRow` is the
  // working copy so Cancel discards. `customRole` switches the role-tag
  // dropdown to its free-text branch.
  const [editIndex, setEditIndex] = useState(-1)
  // The row we just added. The table is grouped by category, so a new teacher
  // lands in the MIDDLE of the list, not at the end — people added someone,
  // saw nothing change, scrolled to the bottom, and concluded it had failed.
  // Scroll to them and flash the row so the add is impossible to miss.
  const [justAdded, setJustAdded] = useState(-1)
  const justAddedRef = useRef(null)
  const [editRow, setEditRow] = useState(blankMember())
  // The design preview trails the typing by a beat, so the fields stay instant
  // while the poster still updates live.
  const [previewRow, setPreviewRow] = useState(blankMember())
  const [customRole, setCustomRole] = useState(false)
  const [fetchingFormPhoto, setFetchingFormPhoto] = useState(false)
  // Shift column (Satya, 28 Oct): each person's latest Keka shift, matched
  // by name. Loaded once; the roster still works if it fails.
  const [shifts, setShifts] = useState({})
  useEffect(() => {
    fetchTeamWallKekaShifts()
      .then((data) => setShifts((data && data.shifts) || {}))
      .catch(() => {})
  }, [])

  // Per-row design dialog: index into members, or -1 closed.
  const [designIndex, setDesignIndex] = useState(-1)

  // Per-row ID card dialog: index into members, or -1 closed.
  const [cardIndex, setCardIndex] = useState(-1)

  const [importingIdForm, setImportingIdForm] = useState(false)
  const [photoProgress, setPhotoProgress] = useState(null) // {done, total}
  const [infoOpen, setInfoOpen] = useState(false)
  const [rosterSearch, setRosterSearch] = useState('')

  // The roster box sizes itself to reach the viewport bottom, so the pinned
  // header works without leaving dead white space below.
  const rosterRef = useRef(null)
  const [rosterMaxH, setRosterMaxH] = useState(null)
  useEffect(() => {
    const el = rosterRef.current
    if (!el) return undefined
    const fit = () => {
      const top = el.getBoundingClientRect().top
      setRosterMaxH(Math.max(260, Math.floor(window.innerHeight - top - 16)))
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [members.length, loadingWall])

  // PDF capture jobs render the off-screen stacks: posters and card faces.
  const [pdfJob, setPdfJob] = useState(null)
  const [cardJob, setCardJob] = useState(null)
  const downloading = !!pdfJob || !!cardJob
  const boardRef = useRef(null)
  const cardRef = useRef(null)

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const data = await fetchScopedCentres()
        const list = data?.results || data || []
        if (!active) return
        setCentres(list)
        setUnrestricted(Boolean(data?.unrestricted))
        setCentreUuid((prev) => {
          const allOk = Boolean(data?.unrestricted)
          if (
            prev &&
            ((prev === ALL_CENTRES && allOk) ||
              list.some((c) => c.uuid === prev))
          ) {
            return prev
          }
          return allOk ? ALL_CENTRES : list[0]?.uuid || ''
        })
      } catch (e) {
        showToast('Could not load centres', constants.ERROR)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const allCentres = centreUuid === ALL_CENTRES

  useEffect(() => {
    if (justAdded < 0) return undefined
    justAddedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const t = setTimeout(() => setJustAdded(-1), 4000)
    return () => clearTimeout(t)
  }, [justAdded])

  useEffect(() => {
    const t = setTimeout(() => setPreviewRow(editRow), 300)
    return () => clearTimeout(t)
  }, [editRow])

  // Load the centre's roster: the most recently modified draft (list is
  // ordered -modified server-side).
  const loadWall = useCallback(async () => {
    if (!centreUuid) return
    setLoadingWall(true)
    try {
      if (centreUuid === ALL_CENTRES) {
        // Photos stripped — this view lists people, it does not build cards.
        const data = await fetchTeamWallHqMembers()
        setDraftUuid('')
        setDraftName('Team Wall')
        setMembers(Array.isArray(data?.members) ? data.members : [])
        return
      }
      const rows = await fetchTeamWallDrafts(centreUuid)
      const latest = Array.isArray(rows) && rows.length ? rows[0] : null
      if (!latest) {
        setDraftUuid('')
        setDraftName('Team Wall')
        setMembers([])
        return
      }
      const draft = await fetchTeamWallDraft(latest.uuid)
      setDraftUuid(latest.uuid)
      setDraftName(latest.name || 'Team Wall')
      setMembers(Array.isArray(draft?.data?.members) ? draft.data.members : [])
    } catch (e) {
      showToast('Could not load the roster', constants.ERROR)
    } finally {
      setLoadingWall(false)
    }
  }, [centreUuid])

  useEffect(() => {
    loadWall()
  }, [loadWall])

  const centre = centres.find((c) => c.uuid === centreUuid)
  const centreName = centre?.name || centre?.centre_name || ''

  /* --------------------------------- save -------------------------------- */

  const saveWall = async (nextMembers) => {
    if (!centreUuid) return
    const list = nextMembers || members
    setSaving(true)
    try {
      const payload = { version: 1, members: list }
      const saved = draftUuid
        ? await updateTeamWallDraft(draftUuid, {
            name: draftName,
            data: payload
          })
        : await saveTeamWallDraft({
            centre: centreUuid,
            name: draftName,
            data: payload
          })
      setDraftUuid(saved.uuid)
      showToast('Roster saved', constants.SUCCESS)
    } catch (e) {
      showToast('Could not save the roster', constants.ERROR)
    } finally {
      setSaving(false)
    }
  }

  /* ------------------------------- members ------------------------------- */

  const openAdd = () => {
    setEditRow(blankMember())
    setCustomRole(false)
    setEditIndex(members.length)
  }

  const openEdit = (index) => {
    const row = { ...blankMember(), ...members[index] }
    setEditRow(row)
    setCustomRole(
      !!row.role && !(ROLE_OPTIONS[row.section] || []).includes(row.role)
    )
    setEditIndex(index)
  }

  // Re-pull this member's ORIGINAL form photo (face-cropped server-side) —
  // for when someone replaced it manually and wants the form one back.
  const fetchFormPhoto = async () => {
    if (!editRow.photoRef) return
    setFetchingFormPhoto(true)
    try {
      const res = await importTeamWallIdPhoto(editRow.photoRef)
      if (res?.photo) {
        setEditRow((r) => ({
          ...r,
          photo: res.photo,
          photoSource: 'form',
          photoV: PHOTO_VERSION,
          photoFaces: res.faces ?? r.photoFaces
        }))
        showToast('Form photo restored', constants.SUCCESS)
      } else {
        showToast('The form has no usable photo for this member', constants.INFO)
      }
    } catch (e) {
      showToast('Could not fetch the form photo', constants.ERROR)
    } finally {
      setFetchingFormPhoto(false)
    }
  }

  // Edits save straight away — the roster has no Save button (auto-save,
  // 2026-07-11 feedback).
  const applyEdit = () => {
    if (!editRow.name.trim()) {
      showToast('The member needs a name', constants.ERROR)
      return
    }
    const isNew = editIndex >= members.length
    const next = [...members]
    next[editIndex] = editRow
    setMembers(next)
    setEditIndex(-1)
    saveWall(next)
    if (isNew) {
      // A search filter would hide the person we just added.
      setRosterSearch('')
      setJustAdded(editIndex)
      showToast(
        `${editRow.name.trim()} added to the roster`,
        constants.SUCCESS
      )
    }
  }

  // Taking someone off the roster RETIRES them; it never deletes the row.
  // The daily ID-form sweep re-adds anyone it cannot match by name, and the
  // form sheet keeps a leaver's response for ever — so a hard delete walked
  // straight back in the next morning. Keeping the record is precisely what
  // makes the import match them and leave them be, while isActive() hides
  // them from every list.
  const removeMember = (index) => {
    const m = members[index]
    if (
      !window.confirm(
        `Take ${m?.name || 'this member'} off the roster?\n\n` +
          'They come off the wall and out of every list. Their record is kept ' +
          'so the daily ID-form import cannot add them back.'
      )
    )
      return
    const next = members.map((x, i) =>
      i === index ? { ...x, active: false } : x
    )
    setMembers(next)
    saveWall(next)
    showToast(`${m?.name || 'Member'} is off the roster`, constants.SUCCESS)
  }

  /* -------------------------------- import ------------------------------- */


  // Queue the org-wide ID-form sweep: EVERY centre's roster gains its people
  // (text + face-cropped photos), merged server-side by name.
  // Why an ID card can't be generated for this member (no face -> no card;
  // junk name = form filled wrongly). photoFaces null = unknown, never blocks.
  const cardIssues = (member) => {
    const issues = []
    if (isJunk(member.name)) {
      issues.push(
        `The form has “${member.name}” as the name — the form was not filled properly; ask them to re-fill it (or fix the name via Edit)`
      )
    }
    if (!member.photo) {
      issues.push(
        'No photo on the roster — import from the ID form or add one via Edit'
      )
    } else if (member.photoFaces === 0) {
      issues.push(
        'No face was detected in the photo — replace it with a clear front-facing photo'
      )
    }
    if (!(member.emergencyName || '').trim()) {
      issues.push('Emergency contact name missing — the card back needs it')
    }
    if (!(member.emergencyPhone || '').trim()) {
      issues.push('Emergency contact number missing — the card back needs it')
    }
    return issues
  }

  // Why a wall poster can't be generated (bio required on teacher/centre-team
  // posters; "NA" bios were cleaned to empty at import).
  const posterIssues = (member) => {
    const issues = []
    const section = SECTION_BY_KEY[member.section] || SECTIONS[0]
    if (isJunk(member.name)) {
      issues.push(
        `The form has “${member.name}” as the name — ask them to re-fill the ID form (or fix the name via Edit)`
      )
    }
    if (section.hasBio && !(member.bio || '').trim()) {
      issues.push(
        'Bio missing on the form — ask them to fill the ID form again, or type the bio here via Edit'
      )
    }
    if (!(member.wallPhoto || member.photo)) {
      issues.push('No photo — import from the ID form or add one via Edit')
    }
    return issues
  }


  const memberKey = (v) =>
    String(v || '')
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .join(' ')

  // All-centres refresh imports every centre's ID form (the beat job's own
  // sweep); a single-centre refresh imports just that one.
  const runIdFormImport = async () => {
    setImportingIdForm(true)
    try {
      // All-centres overview: there is no single draft to merge into, so run
      // the server-side sweep across every centre (the same one the 30-minute
      // beat job runs) and reload the overview.
      if (allCentres) {
        await importTeamWallIdFormAll()
        await loadWall()
        showToast('Rosters refreshed from the ID form', constants.SUCCESS)
        return
      }
      // 1) THIS centre, in-page: text rows land immediately...
      const data = await importTeamWallIdForm(centreUuid)
      const incoming = data?.members || []
      let created = 0
      let updated = 0
      let next = [...members]
      const byName = new Map(next.map((m, i) => [memberKey(m.name), i]))
      incoming.forEach((inc) => {
        const i = byName.get(memberKey(inc.name))
        if (i !== undefined) {
          const cur = next[i]
          next[i] = {
            ...cur,
            phone: inc.phone || cur.phone,
            emergencyName: inc.emergencyName || cur.emergencyName,
            emergencyPhone: inc.emergencyPhone || cur.emergencyPhone,
            cardTitle: inc.cardTitle || cur.cardTitle,
            bio: cur.bio || inc.bio,
            role: cur.role || inc.role,
            photoRef: inc.photoRef || cur.photoRef
          }
          updated += 1
        } else {
          next.push({
            ...blankMember(guessSection(inc.cardTitle || inc.role)),
            ...inc
          })
          created += 1
        }
      })
      setMembers(next)
      if (created || updated) await saveWall(next)
      showToast(
        incoming.length
          ? `ID form: ${created} added, ${updated} updated — fetching photos…`
          : 'No ID-form responses found for this centre',
        incoming.length ? constants.SUCCESS : constants.INFO
      )

      // 2) ...then each photo (server face-crop, ~5s each, 3 at a time).
      const jobs = incoming
        .filter((inc) => inc.photoRef)
        .map((inc) => ({ nameKey: memberKey(inc.name), ref: inc.photoRef }))
      if (jobs.length) {
        let done = 0
        let noFace = 0
        setPhotoProgress({ done: 0, total: jobs.length })
        const queue = [...jobs]
        const worker = async () => {
          for (;;) {
            const job = queue.shift()
            if (!job) return
            try {
              // eslint-disable-next-line no-await-in-loop
              const res = await importTeamWallIdPhoto(job.ref)
              if (res?.faces === 0) noFace += 1
              next = next.map((m) => {
                if (memberKey(m.name) !== job.nameKey) return m
                // Keep hand-uploaded photos; refresh form photos that are
                // missing or processed by an older version (not face-cropped).
                const keep =
                  m.photoSource === 'manual' ||
                  (m.photo && (m.photoV || 0) >= PHOTO_VERSION)
                if (keep) return m
                return {
                  ...m,
                  photo: res?.photo || m.photo,
                  photoSource: res?.photo ? 'form' : m.photoSource,
                  photoV: res?.photo ? PHOTO_VERSION : m.photoV,
                  photoFaces: res?.faces ?? m.photoFaces
                }
              })
              setMembers(next)
            } catch (e) {
              /* photo left empty — re-import retries it */
            }
            done += 1
            setPhotoProgress({ done, total: jobs.length })
          }
        }
        await Promise.all([worker(), worker(), worker()])
        await saveWall(next)
        showToast(
          `Photos: ${done - noFace} ready${
            noFace ? `, ${noFace} with no face detected (card blocked)` : ''
          }`,
          noFace ? constants.INFO : constants.SUCCESS
        )
      }

      // 3) Other centres fill in from the background sweep (best-effort).
      importTeamWallIdFormAll().catch(() => {})
    } catch (e) {
      showToast(
        e?.response?.data?.detail || 'Could not read the ID-card form sheet',
        constants.ERROR
      )
    } finally {
      setPhotoProgress(null)
      setImportingIdForm(false)
    }
  }

  /* --------------------------------- PDF --------------------------------- */

  // Table + full-PDF order: categories in SOP order.
  const orderedMembers = () => {
    const withIndex = members.map((member, i) => ({ member, index: i }))
    const ordered = []
    SECTIONS.forEach((s) => {
      withIndex
        .filter(({ member }) => member.section === s.key)
        .forEach((m) => ordered.push(m))
    })
    return ordered
  }

  // Renders `list` in the off-screen stack, captures each poster at natural
  // size and saves a PDF with the right page size per category.
  const downloadPdf = async (list, filename) => {
    if (!list.length) {
      showToast('Add at least one member first', constants.ERROR)
      return
    }
    setPdfJob({ list, filename })
    // Give the hidden stack a paint before capturing.
    await new Promise((resolve) => setTimeout(resolve, 350))
    try {
      const board = boardRef.current
      const nodes = board
        ? Array.from(board.querySelectorAll('[data-poster]'))
        : []
      if (!nodes.length) throw new Error('nothing rendered')
      const first = SECTION_BY_KEY[list[0].section].pageSize
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: first,
        compress: true
      })
      for (let i = 0; i < list.length; i += 1) {
        const size = SECTION_BY_KEY[list[i].section].pageSize
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(nodes[i], {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          // The photos are remote (Cloudinary). Without useCORS html2canvas
          // cannot read them and the poster comes out with a blank photo.
          useCORS: true
        })
        if (i > 0) pdf.addPage(size, 'p')
        // The artboard is A-series (√2) aspect; the 4x6 photo page is 2:3.
        // Fit the poster inside the page preserving its own aspect (centred,
        // thin white margins) so faces are never stretched or cropped.
        const ar = canvas.width / canvas.height
        let drawW = size[0]
        let drawH = drawW / ar
        if (drawH > size[1]) {
          drawH = size[1]
          drawW = drawH * ar
        }
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 0.92),
          'JPEG',
          (size[0] - drawW) / 2,
          (size[1] - drawH) / 2,
          drawW,
          drawH
        )
      }
      pdf.save(filename)
    } catch (e) {
      showToast('Could not generate the PDF', constants.ERROR)
    } finally {
      setPdfJob(null)
    }
  }


  const downloadOne = (member) => {
    const issues = posterIssues(member)
    if (issues.length) {
      showToast(issues[0], constants.ERROR)
      return
    }
    const centreLabel = centreName ? `${centreName} ` : ''
    downloadPdf(
      [member],
      `${centreLabel}${member.name || 'team member'} poster.pdf`
    )
  }

  // Two-page ID card PDF (front + back) at the template's print ratio.
  const downloadCardPdf = async (member) => {
    const issues = cardIssues(member)
    if (issues.length) {
      showToast(issues[0], constants.ERROR)
      return
    }
    setCardJob({ member })
    await new Promise((resolve) => setTimeout(resolve, 350))
    try {
      const faces = cardRef.current
        ? Array.from(cardRef.current.querySelectorAll('[data-idcard]'))
        : []
      if (faces.length !== 2) throw new Error('card not rendered')
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: CARD_MM,
        compress: true
      })
      for (let i = 0; i < faces.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(faces[i], {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          // The photos are remote (Cloudinary). Without useCORS html2canvas
          // cannot read them and the poster comes out with a blank photo.
          useCORS: true
        })
        if (i > 0) pdf.addPage(CARD_MM, 'p')
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 0.92),
          'JPEG',
          0,
          0,
          CARD_MM[0],
          CARD_MM[1]
        )
      }
      pdf.save(`${member.name || 'team member'} ID card.pdf`)
    } catch (e) {
      showToast('Could not generate the ID card PDF', constants.ERROR)
    } finally {
      setCardJob(null)
    }
  }

  /* --------------------------------- view -------------------------------- */

  const centrePicker = (
    <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
      <InputLabel>Centre</InputLabel>
      <Select
        value={centreUuid}
        onChange={(e) => pickCentre(e.target.value)}
        label="Centre"
      >
        {unrestricted && <MenuItem value={ALL_CENTRES}>All centres</MenuItem>}
        {centres.map((c) => (
          <MenuItem key={c.uuid} value={c.uuid}>
            {c.name || c.centre_name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  const roleOptions = ROLE_OPTIONS[editRow.section] || []
  const roleSelectValue = customRole
    ? '__other'
    : roleOptions.includes(editRow.role)
    ? editRow.role
    : ''

  const searchable = (m) =>
    [
      m.name,
      m.phone,
      m.email,
      m.role,
      m.cardTitle,
      SECTION_BY_KEY[m.section]?.label
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

  // The roster is the people who work here. Leavers stay in the data (their
  // record, photo and card history are kept) but never in the list — an
  // "Inactive" view was a filter nobody wanted to be looking at. Un-tick
  // Active in a member's edit dialog to retire them; that is the only way in
  // or out of this list.
  const ordered = orderedMembers().filter(({ member }) => {
    if (!isActive(member)) return false
    const q = rosterSearch.trim().toLowerCase()
    return !q || searchable(member).includes(q.replace(/\s+/g, ' '))
  })
  const designMember = designIndex >= 0 ? members[designIndex] : null
  const cardMember = cardIndex >= 0 ? members[cardIndex] : null

  return (
    <Box className={classes.boardRoot}>
      <Box className={classes.boardHeader}>
        <Box display="flex" alignItems="center" gridGap={12}>
          <Typography variant="h5">Centre Team Roster</Typography>
          {saving ? (
            <Typography variant="caption" color="textSecondary">
              Saving…
            </Typography>
          ) : null}
        </Box>
        <Box className={classes.headerActions}>
          {centrePicker}
          <TextField
            label="Search name / number / role"
            size="small"
            variant="outlined"
            style={{ minWidth: 230 }}
            value={rosterSearch}
            onChange={(e) => setRosterSearch(e.target.value)}
          />
          {/* Refresh, not "Import from ID form". The roster pulls itself from
              the Teacher ID Cards form every half hour now (same beat job as
              parent onboarding), so importing by hand was doing what was going
              to happen anyway — and the button's existence implied the list
              was stale until you pressed it. Refresh is the "don't make me
              wait for the next run" path. */}
          <Tooltip title="Refresh — pulls the latest people from the Teacher ID Cards form, then reloads the roster">
            <span>
              <IconButton
                size="small"
                    disabled={importingIdForm || !centreUuid}
                onClick={runIdFormImport}
              >
                {importingIdForm ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
          {photoProgress ? (
            <Typography variant="caption" color="textSecondary">
              Photos {photoProgress.done}/{photoProgress.total}…
            </Typography>
          ) : null}
          <Tooltip title="How to print the team wall">
            <IconButton onClick={() => setInfoOpen(true)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          {/* A new member has to be saved INTO a centre's roster, so this needs
              a real centre — "all centres" is a view, not a place to add to. */}
          <Tooltip
            title={
              allCentres
                ? 'Pick a centre first — a new member is added to that centre’s roster'
                : 'Add someone to this centre’s roster'
            }
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={allCentres}
                onClick={openAdd}
              >
                Add team member
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Off-screen stacks: mounted only while a PDF job runs so html2canvas
          has real nodes to capture. */}
      {pdfJob && (
        <div className={classes.hiddenStack}>
          <div ref={boardRef} className={classes.posterStack}>
            {pdfJob.list.map((member, i) => (
              <Poster key={i} classes={classes} member={member} />
            ))}
          </div>
        </div>
      )}
      {cardJob && (
        <div className={classes.hiddenStack}>
          <div ref={cardRef} className={classes.posterStack}>
            <IdCardFront member={cardJob.member} />
            <IdCardBack member={cardJob.member} />
          </div>
        </div>
      )}

      {loadingWall ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper
          ref={rosterRef}
          variant="outlined"
          className={classes.rosterCard}
          style={rosterMaxH ? { maxHeight: rosterMaxH } : undefined}
        >
          <Table size="small" stickyHeader className={classes.rosterTable}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 64 }}>Photo</TableCell>
                <TableCell>Name</TableCell>
                {allCentres ? <TableCell>Centre</TableCell> : null}
                <TableCell>Category</TableCell>
                <TableCell>Role tag</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell align="center">Bio</TableCell>
                <TableCell align="center">Photo</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordered.map(({ member, index }) => {
                const section = SECTION_BY_KEY[member.section] || SECTIONS[0]
                const isJustAdded = index === justAdded
                return (
                  <TableRow
                    key={index}
                    hover
                    ref={isJustAdded ? justAddedRef : undefined}
                    className={isJustAdded ? classes.justAddedRow : ''}
                  >
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={member.photo || undefined}
                      >
                        {(member.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      {member.name}
                      {isJunk(member.name) ? (
                        <Tooltip title="The form was not filled properly — the name field says NA. Ask them to re-fill the ID form, or fix it via Edit.">
                          <Chip
                            size="small"
                            label="form issue"
                            style={{
                              marginLeft: 6,
                              backgroundColor: '#EF5D34',
                              color: '#fff'
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    {allCentres ? (
                      <TableCell>{member.centre || '—'}</TableCell>
                    ) : null}
                    <TableCell>
                      <Chip
                        size="small"
                        label={section.short}
                        style={{
                          backgroundColor: section.color,
                          color: '#fff'
                        }}
                      />
                    </TableCell>
                    <TableCell>{member.role || '—'}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {shiftOf(shifts, member.name) || '—'}
                    </TableCell>
                    <TableCell className={classes.contactCell}>
                      {member.phone || member.email ? (
                        <>
                          {member.phone && <div>{member.phone}</div>}
                          {member.email && <div>{member.email}</div>}
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {(member.bio || '').trim() ? '✓' : '—'}
                    </TableCell>
                    <TableCell align="center">
                      {!member.photo ? (
                        '—'
                      ) : member.photoFaces === 0 ? (
                        <Tooltip title="No face detected — ID card blocked">
                          <span>⚠</span>
                        </Tooltip>
                      ) : (
                        '✓'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* In the all-centres overview the photos are stripped
                          (the payload would be 16 MB otherwise), so a card or a
                          poster built here would come out blank. Offer the one
                          honest action instead: jump to the centre that owns
                          this person, where everything works. */}
                      {allCentres ? (
                        <Tooltip title={`Open ${member.name || 'this person'} in ${member.centre || 'their centre'}`}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!member.centre_uuid}
                              onClick={() => {
                                setRosterSearch(member.name || '')
                                pickCentre(member.centre_uuid)
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                      <>
                      <Tooltip title="View ID card">
                        <IconButton
                          size="small"
                          onClick={() => setCardIndex(index)}
                        >
                          <CreditCardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download ID card PDF">
                        <span>
                          <IconButton
                            size="small"
                            disabled={downloading}
                            onClick={() => downloadCardPdf(member)}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="View wall poster">
                        <IconButton
                          size="small"
                          onClick={() => setDesignIndex(index)}
                        >
                          <PaletteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download poster PDF">
                        <span>
                          <IconButton
                            size="small"
                            disabled={downloading}
                            onClick={() => downloadOne(member)}
                          >
                            <CloudDownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Take off the roster">
                        <IconButton
                          size="small"
                          onClick={() => removeMember(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      </>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              {!ordered.length && (
                <TableRow>
                  <TableCell colSpan={allCentres ? 10 : 9} className={classes.empty}>
                    No team members yet — add one, or hit refresh to pull the
                    ID form.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ------------------------- add/edit dialog ------------------------- */}
      <Dialog
        open={editIndex >= 0}
        onClose={() => setEditIndex(-1)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editIndex < members.length ? 'Edit team member' : 'Add team member'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" gridGap={24}>
            <Box flex={1}>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                className={classes.dialogField}
              >
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={editRow.section}
                  onChange={(e) => {
                    const section = e.target.value
                    setEditRow((r) => ({
                      ...r,
                      section,
                      // Role tags differ per category — keep only if listed.
                      role: (ROLE_OPTIONS[section] || []).includes(r.role)
                        ? r.role
                        : ''
                    }))
                    setCustomRole(false)
                  }}
                >
                  {SECTIONS.map((s) => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                className={classes.dialogField}
                label="Name"
                fullWidth
                size="small"
                variant="outlined"
                value={editRow.name}
                onChange={(e) => {
                  // Read the value BEFORE the updater. React pools synthetic
                  // events: e.target is null by the time a functional updater
                  // runs during render, which threw and unmounted the page
                  // mid-word.
                  const v = e.target.value
                  setEditRow((r) => ({ ...r, name: v }))
                }}
              />
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                className={classes.dialogField}
              >
                <InputLabel>Role tag</InputLabel>
                <Select
                  label="Role tag"
                  value={roleSelectValue}
                  onChange={(e) => {
                    const v = e.target.value
                    if (v === '__other') {
                      setCustomRole(true)
                      setEditRow((r) => ({ ...r, role: '' }))
                    } else {
                      setCustomRole(false)
                      setEditRow((r) => ({ ...r, role: v }))
                    }
                  }}
                >
                  {roleOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                  <MenuItem value="__other">Other…</MenuItem>
                </Select>
              </FormControl>
              {customRole && (
                <TextField
                  className={classes.dialogField}
                  label="Custom role tag"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={editRow.role}
                  onChange={(e) => {
                    // Read the value BEFORE the updater. React pools synthetic
                    // events: e.target is null by the time a functional updater
                    // runs during render, which threw and unmounted the page
                    // mid-word.
                    const v = e.target.value
                    setEditRow((r) => ({ ...r, role: v }))
                  }}
                />
              )}
              <TextField
                className={classes.dialogField}
                label="Phone"
                fullWidth
                size="small"
                variant="outlined"
                value={editRow.phone}
                onChange={(e) => {
                  // Read the value BEFORE the updater. React pools synthetic
                  // events: e.target is null by the time a functional updater
                  // runs during render, which threw and unmounted the page
                  // mid-word.
                  const v = e.target.value
                  setEditRow((r) => ({ ...r, phone: v }))
                }}
              />
              <TextField
                className={classes.dialogField}
                label="Email"
                fullWidth
                size="small"
                variant="outlined"
                value={editRow.email}
                onChange={(e) => {
                  // Read the value BEFORE the updater. React pools synthetic
                  // events: e.target is null by the time a functional updater
                  // runs during render, which threw and unmounted the page
                  // mid-word.
                  const v = e.target.value
                  setEditRow((r) => ({ ...r, email: v }))
                }}
              />
              <Box display="flex" gridGap={8}>
                <TextField
                  className={classes.dialogField}
                  label="Emergency contact name"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={editRow.emergencyName}
                  onChange={(e) => {
                    // Value read BEFORE the updater — React pools the event.
                    const v = e.target.value
                    setEditRow((r) => ({ ...r, emergencyName: v }))
                  }}
                />
                <TextField
                  className={classes.dialogField}
                  label="Emergency contact phone"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={editRow.emergencyPhone}
                  onChange={(e) => {
                    // Value read BEFORE the updater — React pools the event.
                    const v = e.target.value
                    setEditRow((r) => ({ ...r, emergencyPhone: v }))
                  }}
                />
              </Box>
              {SECTION_BY_KEY[editRow.section]?.hasBio && (
                <>
                  <TextField
                    className={classes.dialogField}
                    label="Heading (cool crisp intro)"
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={editRow.heading}
                    onChange={(e) => {
                      // Read the value BEFORE the updater. React pools synthetic
                      // events: e.target is null by the time a functional updater
                      // runs during render, which threw and unmounted the page
                      // mid-word.
                      const v = e.target.value
                      setEditRow((r) => ({ ...r, heading: v }))
                    }}
                  />
                  <TextField
                    className={classes.dialogField}
                    label="Bio (≤200 characters)"
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    variant="outlined"
                    value={editRow.bio}
                    error={(editRow.bio || '').length > 220}
                    helperText={
                      (editRow.bio || '').length > 220
                        ? `${editRow.bio.length} characters — keep it to ≤200`
                        : ''
                    }
                    onChange={(e) => {
                      // Read the value BEFORE the updater. React pools synthetic
                      // events: e.target is null by the time a functional updater
                      // runs during render, which threw and unmounted the page
                      // mid-word.
                      const v = e.target.value
                      setEditRow((r) => ({ ...r, bio: v }))
                    }}
                  />
                </>
              )}
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={isActive(editRow)}
                    onChange={(e) => {
                      // Value read BEFORE the updater — React pools the event.
                      const on = e.target.checked
                      setEditRow((r) => ({ ...r, active: on }))
                    }}
                  />
                }
                label={
                  isActive(editRow)
                    ? 'Active (on the roster)'
                    : 'Inactive — left the organisation'
                }
                className={classes.dialogField}
              />
              <Typography variant="caption" color="textSecondary" component="div">
                ID card photo
              </Typography>
              <Box display="flex" alignItems="center" gridGap={8} mb={1.5}>
                {editRow.photo ? (
                  <img
                    className={classes.photoThumb}
                    src={editRow.photo}
                    alt={editRow.name}
                  />
                ) : null}
                <PhotoPicker
                  label={editRow.photo ? 'Change photo' : 'Add photo'}
                  onPick={(url) =>
                    setEditRow((r) => ({
                      ...r,
                      photo: url,
                      photoSource: 'manual',
                      photoV: PHOTO_VERSION,
                      photoFaces: null
                    }))
                  }
                />
                {editRow.photoRef ? (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={fetchingFormPhoto}
                    startIcon={
                      fetchingFormPhoto ? <CircularProgress size={14} /> : null
                    }
                    onClick={fetchFormPhoto}
                  >
                    {fetchingFormPhoto ? 'Fetching…' : 'Fetch photo from form'}
                  </Button>
                ) : null}
              </Box>
              <Typography variant="caption" color="textSecondary" component="div">
                Wall poster photo (optional — the ID photo is used when empty)
              </Typography>
              <Box display="flex" alignItems="center" gridGap={8}>
                {editRow.wallPhoto ? (
                  <img
                    className={classes.photoThumb}
                    src={editRow.wallPhoto}
                    alt={`${editRow.name} wall`}
                  />
                ) : null}
                <PhotoPicker
                  label={editRow.wallPhoto ? 'Change wall photo' : 'Add wall photo'}
                  onPick={(url) =>
                    setEditRow((r) => ({ ...r, wallPhoto: url }))
                  }
                />
                {editRow.wallPhoto ? (
                  <Button
                    size="small"
                    onClick={() =>
                      setEditRow((r) => ({ ...r, wallPhoto: '' }))
                    }
                  >
                    Use ID photo
                  </Button>
                ) : null}
              </Box>
            </Box>
            {/* The design template follows the category/tag — show it live. */}
            <Box>
              <Typography
                variant="caption"
                color="textSecondary"
                component="div"
                gutterBottom
              >
                Design preview
              </Typography>
              <PosterPreview classes={classes} member={previewRow} scale={0.3} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditIndex(-1)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={applyEdit}>
            {editIndex < members.length ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------- design dialog ------------------------- */}
      <Dialog
        open={designIndex >= 0}
        onClose={() => setDesignIndex(-1)}
        maxWidth="sm"
      >
        <DialogTitle>
          {designMember?.name || 'Poster'} — poster design
        </DialogTitle>
        <DialogContent>
          {designMember && posterIssues(designMember).length ? (
            <Box py={3} px={1}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Wall poster not generated
              </Typography>
              {posterIssues(designMember).map((issue) => (
                <Typography key={issue} variant="body2" gutterBottom>
                  • {issue}
                </Typography>
              ))}
            </Box>
          ) : designMember ? (
            <PosterPreview
              classes={classes}
              member={designMember}
              scale={0.5}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          {designMember && !posterIssues(designMember).length ? (
            <Button
              color="primary"
              variant="contained"
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <GetAppIcon />
                )
              }
              disabled={downloading}
              onClick={() => downloadOne(designMember)}
            >
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          ) : null}
          <Button onClick={() => setDesignIndex(-1)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------- ID card dialog -------------------------- */}
      <Dialog
        open={cardIndex >= 0}
        onClose={() => setCardIndex(-1)}
        maxWidth="md"
      >
        <DialogTitle>{cardMember?.name || 'ID card'} — ID card</DialogTitle>
        <DialogContent>
          {cardMember && cardIssues(cardMember).length ? (
            <Box py={3} px={1}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                ID card not generated
              </Typography>
              {cardIssues(cardMember).map((issue) => (
                <Typography key={issue} variant="body2" gutterBottom>
                  • {issue}
                </Typography>
              ))}
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                style={{ marginTop: 8 }}
                onClick={() => {
                  const idx = cardIndex
                  setCardIndex(-1)
                  openEdit(idx)
                }}
              >
                Fill the missing details
              </Button>
            </Box>
          ) : null}
          {cardMember && !cardIssues(cardMember).length && (
            <Box display="flex" gridGap={16} flexWrap="wrap">
              {[IdCardFront, IdCardBack].map((Face, i) => (
                <div
                  key={i}
                  className={classes.previewFrame}
                  style={{ width: CARD_W * 0.38, height: CARD_H * 0.38 }}
                >
                  <div
                    className={classes.previewInner}
                    style={{ transform: 'scale(0.38)' }}
                  >
                    <Face member={cardMember} />
                  </div>
                </div>
              ))}
            </Box>
          )}
          {cardMember && !(cardMember.emergencyName || cardMember.emergencyPhone) ? (
            <Typography variant="caption" color="error" component="div">
              No emergency contact yet — it arrives with the next ID-form sync
              (or hit refresh), or edit the member.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          {/* Download lives HERE, next to the thing you are looking at. It used
              to be a separate icon back on the row, so getting a card meant
              open → look → close → find the row again → download. */}
          {cardMember && !cardIssues(cardMember).length ? (
            <Button
              color="primary"
              variant="contained"
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <GetAppIcon />
                )
              }
              disabled={downloading}
              onClick={() => downloadCardPdf(cardMember)}
            >
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          ) : null}
          <Button onClick={() => setCardIndex(-1)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------- how-this-works dialog ------------------------- */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Printing the team wall</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This is what you are making — the finished wall at Indiranagar:
          </Typography>
          <img
            src={teamWallReference}
            alt="The finished teacher and team wall at a centre"
            className={classes.wallReference}
          />

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
            Print it yourselves — no vendor, no lamination
          </Typography>
          <Typography variant="body2" component="div">
            <ol style={{ paddingLeft: 20, margin: 0, lineHeight: 1.7 }}>
              <li>
                Open a member’s poster (palette icon on their row) →{' '}
                <b>Download PDF</b>. Do this for everyone you want on the wall.
              </li>
              <li>
                Print on the <b>6×4&quot; (4R) photo paper</b> you have been
                given — one poster per sheet. Set scale to <b>100%</b> (“actual
                size”), never “fit to page”, or the poster comes out the wrong
                size.
              </li>
              <li>
                <b>No lamination needed</b> — the photo paper already has the
                finish. Just trim any white edges if you like.
              </li>
              <li>
                Mount on the softboard near reception. Put the posters up first,
                then size the headings to the space that is left.
              </li>
            </ol>
          </Typography>

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
            ID cards are not a centre job — HQ prints those
          </Typography>
          <Typography variant="body2">
            Who prints what: the <b>wall posters you print yourselves</b>, at the
            centre. <b>ID cards are printed by HQ</b> — download the person’s ID
            card PDF (card icon on their row) and raise an{' '}
            <b>issue-tracker request to the Procurement team</b> with the PDFs
            attached. Do not print or laminate ID cards at the centre.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default TeamWall
